import { memo, useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Avatar, Badge } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import _get from "lodash/get";
import _debounce from "lodash/debounce";
import _isEmpty from "lodash/isEmpty";
import DefaultLogo from "assets/images/logovietgrp.svg";
import { useSocket } from "hooks";
import {
  toggleList,
  setUnreadCount,
  openChatWindow,
  closeList,
  popupNewMessage,
  popupGetConversationListRequest,
  popupMarkMessageRecalled,
  popupUpdateMessageText,
  popupUpdateUserStatus,
  popupIncrementUnseen,
} from "providers/ChatPopupProvider/slice";
import ChatPopupList from "./ChatPopupList";
import ChatPopupWindow from "./ChatPopupWindow";
import "./styles.less";

const ChatPopup = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { socket, isConnected } = useSocket();

  // Responsive: detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxUnreadBubbles = isMobile ? 3 : 5;

  const currentUserId = useSelector((state) =>
    _get(state, "auth.currentUser._id")
  );
  const isListOpen = useSelector((state) => state.chatPopup.isListOpen);
  const openWindows = useSelector((state) => state.chatPopup.openWindows);
  const fonehouseUnreadCount = useSelector(
    (state) => state.chatPopup.fonehouseUnreadCount
  );
  const lastSentMessage = useSelector(
    (state) => state.chatPopup.lastSentMessage
  );
  const conversationEntities = useSelector(
    (state) => state.chatPopup.conversations.entities
  );
  const conversationIds = useSelector(
    (state) => state.chatPopup.conversations.ids
  );

  // Hide on full-page messenger route
  const isOnMessengerPage = location.pathname.startsWith(
    "/fonehouse/messengers"
  );

  // Derive unread bubbles from conversation list (persistent, no timeout)
  const unreadBubbles = useMemo(() => {
    return conversationIds
      .map((id) => conversationEntities[id])
      .filter((conv) => {
        if (!conv || !conv.unseenMessages) return false;
        // Don't show bubble if chat window for this conv is already open & not minimized
        const win = openWindows.find((w) => w.conversationId === conv._id);
        if (win && !win.minimized) return false;
        return true;
      })
      .slice(0, maxUnreadBubbles)
      .map((conv) => {
        const user = conv.users?.[0];
        const senderName = user?.name || "Người dùng";
        const avatar = _get(user, "userDetail.avatar") || DefaultLogo;
        const latestMsg = conv.latestMessage;

        let text = latestMsg?.text;
        if (!_isEmpty(latestMsg?.attachments)) {
          text = "Đã gửi tệp đính kèm";
        } else if (latestMsg?.type === "product") {
          text = "Đã gửi sản phẩm";
        }

        return {
          conversationId: conv._id,
          senderName,
          avatar,
          text: text || "Tin nhắn mới",
          unseenCount: conv.unseenMessages,
        };
      });
  }, [conversationIds, conversationEntities, openWindows, maxUnreadBubbles]);

  // Derive unread conversation count from conversation list (instant, no socket wait)
  const derivedUnreadCount = useMemo(() => {
    return conversationIds.filter((id) => {
      const conv = conversationEntities[id];
      return conv && conv.unseenMessages > 0;
    }).length;
  }, [conversationIds, conversationEntities]);

  // Use whichever is higher: socket count or derived count
  const displayUnreadCount = Math.max(fonehouseUnreadCount, derivedUnreadCount);

  // Click bubble -> open chat window directly
  const handleBubbleClick = useCallback(
    (bubble) => {
      dispatch(openChatWindow({ conversationId: bubble.conversationId }));
      dispatch(closeList());
    },
    [dispatch]
  );

  // Fetch conversation list on mount so bubbles + badge show immediately
  useEffect(() => {
    if (currentUserId) {
      dispatch(popupGetConversationListRequest({ limit: 30, page: 1 }));
    }
  }, [dispatch, currentUserId]);

  // Debounced refresh for conversation list
  const debouncedRefreshList = useMemo(
    () =>
      _debounce(
        () => {
          dispatch(
            popupGetConversationListRequest({ limit: 30, page: 1 })
          );
        },
        500,
        { leading: false, trailing: true }
      ),
    [dispatch]
  );

  useEffect(() => {
    return () => debouncedRefreshList.cancel();
  }, [debouncedRefreshList]);

  // Socket: listen for unread count updates + request initial count
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      // Request unread count immediately on connect
      socket.emit("get-unread-conversations");

      const handleUnreadConversations = (data) => {
        const count = _get(data, "unreadConversations.FONEHOUSE_TO_USER", 0);
        dispatch(setUnreadCount(count));
      };

      socket.on("unread-conversations", handleUnreadConversations);
      return () => {
        socket.off("unread-conversations", handleUnreadConversations);
      };
    }
  }, [currentUserId, socket, isConnected, dispatch]);

  // Socket: subscribe to open window rooms + handle new_message
  useEffect(() => {
    if (!currentUserId || !socket || !isConnected) return;

    // Subscribe to all open window rooms
    openWindows.forEach((w) => {
      socket.emit("subscribe", { room: w.conversationId });
    });

    const handleNewMessage = (data) => {
      const message = _get(data, "message");
      const convId = message?.conversationId;
      if (!convId) return;

      // Dispatch to matching open window
      const matchingWindow = openWindows.find(
        (w) => w.conversationId === convId
      );
      if (matchingWindow) {
        dispatch(popupNewMessage({ conversationId: convId, message }));
      }

      // Increment unseen if message is from user (not admin)
      if (message.isAdmin !== 1) {
        if (!matchingWindow || matchingWindow.minimized) {
          dispatch(popupIncrementUnseen({ conversationId: convId }));
        }
      }

      // Refresh conversation list to update ordering/previews
      debouncedRefreshList();
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("new_message", handleNewMessage);
      openWindows.forEach((w) => {
        socket.emit("unsubscribe", { room: w.conversationId });
      });
    };
  }, [currentUserId, socket, isConnected, openWindows, dispatch, debouncedRefreshList]);

  // Socket: message-recalled
  useEffect(() => {
    if (!currentUserId || !socket || !isConnected) return;
    const handler = (data) => {
      dispatch(popupMarkMessageRecalled({ messageId: data.messageId }));
    };
    socket.on("message-recalled", handler);
    return () => socket.off("message-recalled", handler);
  }, [currentUserId, socket, isConnected, dispatch]);

  // Socket: message-updated
  useEffect(() => {
    if (!currentUserId || !socket || !isConnected) return;
    const handler = (data) => {
      dispatch(popupUpdateMessageText(data));
    };
    socket.on("message-updated", handler);
    return () => socket.off("message-updated", handler);
  }, [currentUserId, socket, isConnected, dispatch]);

  // Socket: user-status-change
  useEffect(() => {
    if (!currentUserId || !socket || !isConnected) return;
    const handler = (data) => {
      dispatch(popupUpdateUserStatus(data));
    };
    socket.on("user-status-change", handler);
    return () => socket.off("user-status-change", handler);
  }, [currentUserId, socket, isConnected, dispatch]);

  // Socket: emit new_message_admin when popup sends a message
  useEffect(() => {
    if (lastSentMessage && socket && isConnected) {
      socket.emit("new_message_admin", {
        room: lastSentMessage.conversationId,
        messageId: lastSentMessage.messageId,
      });
    }
  }, [lastSentMessage, socket, isConnected]);

  // Check if there's a non-minimized chat window open
  const hasOpenWindow = openWindows.some((w) => !w.minimized);
  // Collapse bubbles (avatar only) when a chat window or list is open
  const collapsedBubbles = hasOpenWindow || isListOpen;

  const handleToggleList = useCallback(() => {
    dispatch(toggleList());
  }, [dispatch]);

  // Don't render when on full-page messenger or not logged in
  if (isOnMessengerPage || !currentUserId) return null;

  return (
    <div className="chat-popup-container">
      {/* Persistent unread conversation bubbles - hide when fullscreen chat on mobile */}
      {unreadBubbles.length > 0 && !(isMobile && hasOpenWindow) && (
        <div className={`chat-popup-unread-stack ${collapsedBubbles ? "collapsed" : ""}`}>
          {unreadBubbles.map((bubble) => (
            <div
              key={bubble.conversationId}
              className="chat-popup-unread-bubble"
              onClick={() => handleBubbleClick(bubble)}
              title={collapsedBubbles ? `${bubble.senderName}: ${bubble.text}` : undefined}
            >
              <Badge count={bubble.unseenCount} size="small" offset={[-4, 4]}>
                <Avatar size={collapsedBubbles ? 36 : (isMobile ? 38 : 44)} src={bubble.avatar} />
              </Badge>
              {!collapsedBubbles && (
                <div className="unread-bubble-preview">
                  <div className="unread-bubble-name">{bubble.senderName}</div>
                  <div className="unread-bubble-text">{bubble.text}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Floating icon - hide when fullscreen chat on mobile */}
      {!(isMobile && hasOpenWindow) && (
        <div className="chat-popup-fab" onClick={handleToggleList}>
          <Badge count={displayUnreadCount} size="small" offset={[-2, 2]}>
            <MessageOutlined className="chat-popup-fab-icon" />
          </Badge>
        </div>
      )}

      {/* Conversation list popup */}
      {isListOpen && <ChatPopupList />}

      {/* Chat windows - on mobile only show the latest non-minimized */}
      {(isMobile ? openWindows.slice(-1) : openWindows).map((w, index) => (
        <ChatPopupWindow
          key={w.conversationId}
          conversationId={w.conversationId}
          minimized={w.minimized}
          index={index}
        />
      ))}
    </div>
  );
};

export default memo(ChatPopup);
